import React from "react";
import { Link } from "react-router-dom";
import {
  Scissors,
  Hammer,
  Forklift,
  Truck,
  Award,
  Briefcase,
  Euro,
  Accessibility,
  Users,
  UserCog,
  Mail,
  Calendar,
  Clock,
  Download,
  BadgeCheck,
  TrendingUp,
  GraduationCap,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import { PageHero, Section, Card } from "@/components/Section";
import { FAQ } from "@/components/FAQ";
import { UFA_FAQ } from "@/lib/faqData";
import { IMAGES } from "@/lib/site";

// 9 formations + BPJEPS — données officielles UFA Wresinski (CFA ECML)
const SECTEURS = [
  {
    key: "coiffure",
    nom: "Coiffure",
    icon: Scissors,
    couleur: "bg-brand-sand-dark text-white",
    formations: [
      { titre: "Brevet Professionnel Coiffure", niveau: "BP — Niveau 4", rncp: "RNCP 38231" },
      { titre: "Certificat de Spécialisation Coiffure Coupe Couleur", niveau: "CS — Niveau 4", rncp: "RNCP 38232" },
    ],
  },
  {
    key: "batiment",
    nom: "Bâtiment",
    icon: Hammer,
    couleur: "bg-brand-slate text-brand-cream",
    formations: [
      { titre: "Titre Pro Agent de Maintenance des Bâtiments", niveau: "TP — Niveau 3", rncp: "RNCP 42031" },
      { titre: "BTS Management économique de la construction", niveau: "BTS — Bac +2", rncp: "RNCP 35802" },
      { titre: "BTS Enveloppe des bâtiments : conception et réalisation", niveau: "BTS — Bac +2", rncp: "RNCP 37495" },
    ],
  },
  {
    key: "logistique",
    nom: "Logistique",
    icon: Forklift,
    couleur: "bg-brand-sage-dark text-white",
    formations: [
      { titre: "Technicien en Logistique d'Entreposage", niveau: "TP — Niveau 4", rncp: "RNCP 36237" },
      { titre: "Technicien Supérieur en Méthodes et Exploitation Logistique", niveau: "TP — Niveau 5 (Bac +2)", rncp: "RNCP 37277" },
    ],
  },
  {
    key: "transport",
    nom: "Transport",
    icon: Truck,
    couleur: "bg-brand-slate-dark text-brand-cream",
    formations: [
      { titre: "CAP Conducteur routier de marchandises", niveau: "CAP — Niveau 3", rncp: "RNCP 37894" },
      { titre: "CAP Conducteur Agent d'Accueil en Autobus et Autocar (C4A)", niveau: "CAP — Niveau 3", rncp: null },
      { titre: "Gestionnaire des opérations de transport routier de marchandises", niveau: "TP — Niveau 5 (Bac +2)", rncp: "RNCP 40990" },
    ],
  },
  {
    key: "animation",
    nom: "Animation & Sport",
    icon: Sparkles,
    couleur: "bg-brand-sand text-brand-slate-dark",
    formations: [
      { titre: "BPJEPS — Brevet Professionnel de la Jeunesse, de l'Éducation Populaire et du Sport", niveau: "BP — Niveau 4", rncp: null },
    ],
  },
];

const atouts = [
  { icon: Euro, t: "100 % gratuit", d: "Aucun frais d'inscription, ni de scolarité, ni d'examen." },
  { icon: Users, t: "Pour tous", d: "Collégiens, lycéens, en reconversion ou demandeurs d'emploi : l'apprentissage est ouvert à tous." },
  { icon: Accessibility, t: "Accessible PSH", d: "Toutes nos formations sont accessibles aux personnes en situation de handicap." },
  { icon: BadgeCheck, t: "CFA certifié QUALIOPI", d: "Le CFA ECML est certifié QUALIOPI — gage de qualité de nos formations." },
];

const rythmes = [
  { secteur: "Coiffure", rythme: "2 jours en UFA / 3 jours en salon", info: "Présence salon en continu avec retours hebdomadaires en formation." },
  { secteur: "Bâtiment (TP & BTS)", rythme: "1 semaine UFA / 2 à 3 semaines entreprise", info: "Rythme adapté aux chantiers et aux périodes d'examen." },
  { secteur: "Logistique", rythme: "2 jours UFA / 3 jours entreprise", info: "Immersion régulière sur les plateformes logistiques." },
  { secteur: "Transport", rythme: "1 semaine UFA / 2 semaines entreprise", info: "Compatible avec les contraintes de tournée et de réglementation." },
  { secteur: "BPJEPS", rythme: "1 jour UFA / 4 jours structure", info: "Présence forte sur le terrain pour la gestion d'activités sportives." },
];

const equipe = [
  { nom: "Jean-Baptiste BL", role: "Responsable de l'UFA", mail: "responsable@lycee.fr", icon: UserCog },
  { nom: "Aurore DU", role: "Coordinatrice de l'UFA", mail: "coordinatrice@lycee.fr", icon: Users },
];

const stats = [
  { icon: TrendingUp, val: "95 %", lbl: "Taux de satisfaction", info: "Promotion 2025 — sur 114 répondants" },
  { icon: Award, val: "84 %", lbl: "Taux de réussite", info: "Promotion 2025 — sur 45 inscrits à l'examen" },
  { icon: Briefcase, val: "45", lbl: "Apprentis sortants en 2025", info: "Poursuite d'études ou insertion professionnelle" },
];

export default function Alternance() {
  return (
    <div data-testid="alternance-page">
      <PageHero
        eyebrow="UFA Wresinski · CFA ECML"
        title="L'alternance pour construire son métier en grandeur réelle"
        subtitle="Créée en 2018, l'UFA Wresinski est l'unité de formation d'apprentis du CFA ECML. Elle prépare 9 formations diplômantes du CAP au Bac +2, dans 4 secteurs d'activité : coiffure, bâtiment, logistique et transport."
        image={IMAGES.workshop}
      >
        <div className="mt-2 flex flex-wrap gap-3">
          <a
            href="#brochure"
            data-testid="alternance-cta-brochure"
            className="inline-flex items-center gap-2 rounded-full bg-brand-sand-dark px-5 py-3 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-sand"
          >
            <Download className="h-4 w-4" />
            Télécharger la brochure
          </a>
          <Link
            to="/contact?type=inscription"
            data-testid="alternance-cta-postuler"
            className="inline-flex items-center gap-2 rounded-full border border-brand-cream/30 px-5 py-3 text-sm font-semibold text-brand-cream transition-all duration-300 hover:bg-brand-cream hover:text-brand-slate-dark"
          >
            Candidater
          </Link>
        </div>
      </PageHero>

      {/* Atouts */}
      <Section eyebrow="L'apprentissage à l'UFA Wresinski" title="Une formation diplômante 100 % gratuite, ouverte à tous">
        <p className="mb-10 max-w-3xl text-base leading-relaxed text-muted-foreground">
          L'UFA Wresinski propose un enseignement en apprentissage ouvert à un public âgé de
          <strong className="text-brand-slate-dark"> 15 à 29 ans</strong>*. Préparer son projet
          professionnel en découvrant le monde de l'entreprise : c'est la promesse de l'apprentissage,
          quel que soit votre parcours initial.
        </p>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {atouts.map((a, i) => (
            <Card key={a.t} data-testid={`alternance-atout-${i}`}>
              <span className="grid h-12 w-12 place-items-center rounded-2xl bg-brand-slate/10 text-brand-slate-dark">
                <a.icon className="h-5 w-5" />
              </span>
              <h3 className="mt-5 font-heading text-lg font-semibold text-brand-slate-dark">{a.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{a.d}</p>
            </Card>
          ))}
        </div>
        <p className="mt-6 text-xs text-muted-foreground">
          * L'apprenti doit avoir 29 ans à la date de signature du contrat d'apprentissage.
        </p>
      </Section>

      {/* Chiffres clés 2025 */}
      <Section wrapper="bg-brand-slate-dark text-brand-cream" eyebrow="Indicateurs 2025" title="Des résultats qui parlent pour nous">
        <div className="grid gap-6 md:grid-cols-3">
          {stats.map((s, i) => (
            <div
              key={s.lbl}
              data-testid={`alternance-stat-${i}`}
              className="rounded-3xl border border-white/10 bg-white/5 p-8"
            >
              <s.icon className="h-7 w-7 text-brand-sand" />
              <p className="mt-5 font-heading text-5xl font-bold text-brand-cream">{s.val}</p>
              <p className="mt-2 font-heading text-base font-semibold text-brand-cream">{s.lbl}</p>
              <p className="mt-1 text-xs text-brand-cream/70">{s.info}</p>
            </div>
          ))}
        </div>
        <p className="mt-8 text-xs uppercase tracking-wider text-brand-sand">
          CFA ECML — Certifié QUALIOPI
        </p>
      </Section>

      {/* Formations par secteur */}
      <Section
        id="formations"
        eyebrow="Nos formations"
        title="9 diplômes du CAP au Bac +2, dans 4 secteurs"
      >
        <div className="grid gap-6 lg:grid-cols-2">
          {SECTEURS.map((s, idx) => (
            <Card key={s.key} data-testid={`secteur-${s.key}`}>
              <div className="flex items-center gap-3">
                <span className={`grid h-12 w-12 place-items-center rounded-2xl ${s.couleur}`}>
                  <s.icon className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Secteur {idx + 1}
                  </p>
                  <h3 className="font-heading text-xl font-bold text-brand-slate-dark">{s.nom}</h3>
                </div>
              </div>
              <ul className="mt-5 space-y-3">
                {s.formations.map((f) => (
                  <li
                    key={f.titre}
                    className="rounded-2xl border border-black/5 bg-brand-sand-wrapper/40 p-4"
                  >
                    <p className="font-heading text-sm font-semibold text-brand-slate-dark">
                      {f.titre}
                    </p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <span className="rounded-full bg-brand-cream px-2.5 py-0.5 font-medium">
                        {f.niveau}
                      </span>
                      {f.rncp && (
                        <span className="font-medium tabular-nums text-brand-sand-dark">{f.rncp}</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>
      </Section>

      {/* Rythmes d'alternance */}
      <Section
        wrapper="bg-brand-sand-wrapper/40"
        eyebrow="Rythmes d'alternance"
        title="Une organisation pensée pour l'apprenant et l'entreprise"
      >
        <div className="overflow-hidden rounded-3xl border border-black/5 bg-brand-cream shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm" data-testid="rythmes-table">
              <thead>
                <tr className="bg-brand-slate-dark text-brand-cream">
                  <th className="px-5 py-4 text-left font-heading font-semibold">Secteur / Formation</th>
                  <th className="px-5 py-4 text-left font-heading font-semibold">Rythme</th>
                  <th className="px-5 py-4 text-left font-heading font-semibold">Précisions</th>
                </tr>
              </thead>
              <tbody>
                {rythmes.map((r, i) => (
                  <tr
                    key={r.secteur}
                    data-testid={`rythme-${i}`}
                    className="border-t border-black/5 transition-colors hover:bg-brand-sand-wrapper/30"
                  >
                    <td className="px-5 py-4 font-medium text-brand-slate-dark">{r.secteur}</td>
                    <td className="px-5 py-4">
                      <span className="inline-flex items-center gap-2 rounded-full bg-brand-sage/15 px-3 py-1 text-xs font-semibold text-brand-sage-dark">
                        <Clock className="h-3.5 w-3.5" />
                        {r.rythme}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-muted-foreground">{r.info}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <p className="mt-4 text-xs text-muted-foreground">
          Les rythmes peuvent légèrement varier selon les promotions et les besoins de l'entreprise.
          Le calendrier précis est communiqué à la signature du contrat.
        </p>
      </Section>

      {/* Équipe */}
      <Section eyebrow="Une équipe à votre écoute" title="Vos interlocuteurs dédiés">
        <div className="grid gap-6 md:grid-cols-2">
          {equipe.map((p, i) => (
            <Card key={p.mail} data-testid={`ufa-equipe-${i}`}>
              <div className="flex items-center gap-4">
                <span className="grid h-14 w-14 place-items-center rounded-full bg-brand-slate text-brand-cream">
                  <p.icon className="h-6 w-6" />
                </span>
                <div>
                  <p className="font-heading text-lg font-bold text-brand-slate-dark">{p.nom}</p>
                  <p className="text-sm text-muted-foreground">{p.role}</p>
                </div>
              </div>
              <a
                href={`mailto:${p.mail}`}
                data-testid={`ufa-equipe-mail-${i}`}
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-brand-sand-wrapper px-4 py-2.5 text-sm font-semibold text-brand-slate-dark transition-colors hover:bg-brand-sand"
              >
                <Mail className="h-4 w-4" />
                {p.mail}
              </a>
            </Card>
          ))}
        </div>
      </Section>

      {/* Brochure & CTA final */}
      <Section id="brochure" wrapper="bg-brand-slate text-brand-cream">
        <div className="grid items-center gap-10 lg:grid-cols-[1.3fr_1fr]">
          <div>
            <Calendar className="h-8 w-8 text-brand-sand" />
            <h2 className="mt-4 font-heading text-3xl font-bold sm:text-4xl">
              Rejoignez l'UFA Wresinski
            </h2>
            <p className="mt-4 max-w-2xl text-brand-cream/85">
              Téléchargez la brochure complète de l'UFA Wresinski pour découvrir l'ensemble des
              formations, les modalités d'admission et les rythmes d'alternance par secteur.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-brand-cream/85">
              {[
                "9 formations diplômantes du CAP au Bac +2",
                "Apprentissage 100 % gratuit pour les apprentis",
                "Accompagnement personnalisé recherche d'entreprise",
                "Public accueilli de 15 à 29 ans",
              ].map((it) => (
                <li key={it} className="flex items-start gap-2">
                  <GraduationCap className="mt-0.5 h-4 w-4 shrink-0 text-brand-sand" />
                  {it}
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-3xl border border-white/10 bg-white/5 p-8">
            <p className="text-xs font-semibold uppercase tracking-wider text-brand-sand">
              Document officiel
            </p>
            <h3 className="mt-3 font-heading text-2xl font-bold text-brand-cream">
              Brochure UFA Wresinski 2026
            </h3>
            <p className="mt-3 text-sm text-brand-cream/80">
              PDF · 12 pages · Mise à jour décembre 2025
            </p>
            <a
              href="/brochure-ufa-wresinski.pdf"
              download
              data-testid="alternance-brochure-download"
              className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-sand-dark px-5 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-sand"
            >
              <Download className="h-4 w-4" />
              Télécharger la brochure
            </a>
            <Link
              to="/contact?type=inscription"
              data-testid="alternance-cta-rdv"
              className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full border border-brand-cream/30 px-5 py-3.5 text-sm font-semibold text-brand-cream transition-all duration-300 hover:bg-brand-cream hover:text-brand-slate-dark"
            >
              Prendre rendez-vous
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </Section>

      {/* FAQ UFA — SEO/GEO/GSO */}
      <FAQ
        items={UFA_FAQ}
        eyebrow="FAQ Apprentissage"
        title="Tout savoir sur l'apprentissage à l'UFA Wresinski"
        subtitle="Conditions, rythmes, rémunération, candidature : nos réponses aux questions les plus posées par les apprentis et les entreprises."
        schemaId="faq-ufa"
      />
    </div>
  );
}
